/**
 * Amazon buy button — shown when the novel has an Amazon purchase link.
 * Falls back to a discreet ISBN badge when no Amazon URL is available yet,
 * so upcoming titles ("À paraître") still expose their catalog identifier
 * to librarians, booksellers and pro visitors.
 *
 * `locale` — "en" renders the English label ("View on Amazon" / "Upcoming"),
 *            any other value keeps the historical French labels.
 */
export default function AmazonBuyButton({ novel, className = "", compact = false, locale = "fr" }) {
  const isEn = locale === "en";
  // For English visitors, prefer amazon.com (or any `amazon_com` store) over amazon.fr.
  const links = novel?.purchase_links || [];
  const amazonCom = links.find((l) => l?.store === "amazon_com" && l?.url);
  const amazonFr = links.find((l) => l?.store === "amazon" && l?.url);
  const amazon = isEn ? (amazonCom || amazonFr) : (amazonFr || amazonCom);

  const isbn = novel?.isbn;
  const upcoming = novel?.commercial_availability === false;
  const releaseYear = novel?.planned_release;
  const upcomingLabel = isEn
    ? (releaseYear ? `Upcoming · Release ${releaseYear}` : "Upcoming")
    : (releaseYear ? `À paraître · Sortie prévue en ${releaseYear}` : "À paraître");
  const upcomingCompactLabel = isEn
    ? (releaseYear ? `Upcoming · ${releaseYear}` : "Upcoming")
    : (releaseYear ? `À paraître · ${releaseYear}` : "À paraître");
  const availableCompact = isEn ? "View on Amazon" : "Disponible sur Amazon";
  const buyFullLabel = isEn ? "View on Amazon" : "Acheter sur Amazon";

  // Compact variant (used in BookCard grid)
  if (compact) {
    if (upcoming) {
      return (
        <span
          data-testid="novel-upcoming-badge"
          className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--copper)] ${className}`}
        >
          {upcomingCompactLabel}
        </span>
      );
    }
    if (amazon) {
      return (
        <a
          href={amazon.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          data-testid="amazon-buy-link"
          className={`inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[var(--copper)] hover:gap-3 transition-all duration-300 ${className}`}
        >
          {availableCompact} <span aria-hidden>→</span>
        </a>
      );
    }
    if (isbn) {
      return (
        <span
          data-testid="novel-isbn-badge"
          className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--brown)]/70 ${className}`}
        >
          ISBN {isbn}
        </span>
      );
    }
    return null;
  }

  // Full-width variant (used in NovelDetail hero)
  if (upcoming) {
    return (
      <div
        data-testid="novel-upcoming-block"
        className={`w-full text-center border border-[var(--copper)]/60 px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-[var(--copper)] ${className}`}
      >
        {upcomingLabel}
        {isbn && (
          <div className="mt-1 text-[10px] text-[var(--brown)]/70 normal-case tracking-normal">
            <span className="opacity-60 mr-1">ISBN</span>
            <span className="text-[var(--ink)]/80">{isbn}</span>
          </div>
        )}
      </div>
    );
  }
  if (amazon) {
    return (
      <a
        href={amazon.url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        data-testid="amazon-buy-button"
        className={`group block w-full text-center border border-[var(--copper)] bg-[var(--copper)] text-[var(--ivory)] px-6 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-transparent hover:text-[var(--copper)] transition-all duration-500 ${className}`}
      >
        {buyFullLabel}
        <span aria-hidden className="ml-2 inline-block transition-transform group-hover:translate-x-1">→</span>
      </a>
    );
  }
  if (isbn) {
    return (
      <div
        data-testid="novel-isbn-block"
        className={`w-full text-center border border-[var(--line)] px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-[var(--brown)]/80 ${className}`}
      >
        <span className="opacity-60 mr-2">ISBN</span>
        <span className="text-[var(--ink)]">{isbn}</span>
      </div>
    );
  }
  return null;
}
